import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { grantsService } from '../services/api';
import GrantForm from './GrantForm';
import NoteForm from './NoteForm';
import AuditHistory from './AuditHistory';
import AttachmentsTab from './Attachments';
import './GrantDetail.css';
import { useAuth } from '../contexts/AuthContext';
import { formatPropertyName } from '../utils/strings';
import axios from 'axios';

const GrantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [grant, setGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [attachmentCount, setAttachmentCount] = useState(0);
  const { isAdmin, accessToken } = useAuth();

  const fetchGrant = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await grantsService.get(id);
      setGrant(response.data);
      
      // Fetch attachment count
      try {
        const attachmentResponse = await axios.get(
          `/api/attachments?entityType=grant&entityId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        setAttachmentCount(attachmentResponse.data.data?.length || 0);
      } catch (attachmentErr) {
        console.error('Error fetching attachments:', attachmentErr);
        setAttachmentCount(0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch grant details');
      console.error('Error fetching grant:', err);
    } finally {
      setLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchGrant();
  }, [fetchGrant]);

  const handleDeleteGrant = async () => {
    if (window.confirm('Are you sure you want to delete this grant?')) {
      try {
        await grantsService.delete(id);
        alert('Grant deleted successfully');
        navigate('/grants');
      } catch (err) {
        alert('Failed to delete grant: ' + err.message);
      }
    }
  };

  const handleEditSubmit = () => {
    setShowEditForm(false);
    fetchGrant();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return '#ff9800';
      case 'approved':
        return '#4caf50';
      case 'rejected':
        return '#f44336';
      case 'in_progress':
        return '#2196f3';
      case 'funded':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  if (loading) return <div className="loading">Loading grant details...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!grant) return <div className="alert alert-error">Grant not found</div>;

  return (
    <div className="grant-detail-container">
      <div className="detail-header">
        <button className="btn btn-outline" onClick={() => navigate('/grants')}>
          ← Back to Grants
        </button>
        <div className="header-content">
          <h1>{grant.grant_name}</h1>
          <div className="header-meta">
            <span 
              className="status-badge"
              style={{ backgroundColor: getStatusColor(grant.status) }}
            >
              {formatPropertyName(grant.status)}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-outline"
            onClick={() => setShowEditForm(true)}
          >
            Edit
          </button>
          {isAdmin && (<button 
              className="btn btn-danger"
              onClick={handleDeleteGrant}
            >
              Delete Grant
            </button>)}
        </div>
      </div>

      {showEditForm && (
        <GrantForm 
          initialData={grant}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditForm(false)}
        />
      )}

      <div className="detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes ({grant.notes?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'attachments' ? 'active' : ''}`}
          onClick={() => setActiveTab('attachments')}
        >
          Attachments ({attachmentCount})
        </button>
        {isAdmin && (
          <button 
            className={`tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit History
          </button>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className="detail-section">
          <div className="info-grid">
            <div className="info-card">
              <label>Organization</label>
              <p>{grant.organization_name || 'Not assigned'}</p>
            </div>

            <div className="info-card">
              <label>Grant Amount</label>
              {grant.is_in_kind ? (
                <p className="amount in-kind-badge">In-Kind Grant</p>
              ) : (
                <p className="amount">
                  {(Number(grant.amount_requested) || 0).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  })}
                </p>
              )}
            </div>

            <div className="info-card">
              <label>Application Date</label>
              <p>{new Date(grant.date_applied).toLocaleDateString()}</p>
            </div>

            <div className="info-card">
              <label>Current Status</label>
              <p className="status">{formatPropertyName(grant.status)}</p>
            </div>

            <div className="info-card">
              <label>Created</label>
              <p>{new Date(grant.created_at).toLocaleDateString()}</p>
            </div>

            <div className="info-card">
              <label>Last Updated</label>
              <p>{new Date(grant.updated_at).toLocaleDateString()}</p>
            </div>
          </div>

          {grant.description && (
            <div className="description-section">
              <h3>Description</h3>
              <p>{grant.description}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="detail-section">
          <NoteForm 
            entityId={parseInt(id)} 
            entityType="grant"
            onNoteAdded={fetchGrant}
          />
        </div>
      )}

      {activeTab === 'attachments' && (
        <AttachmentsTab 
          entityType="grant" 
          entityId={parseInt(id)}
          token={accessToken}
        />
      )}

      {activeTab === 'audit' && (
        <AuditHistory entityId={id} entityType="grant" />
      )}
    </div>
  );
};

export default GrantDetail;
